import { DEFAULT_EXTRACTION_FACETS, TaxonomyFacet } from '@edgebox/sync-core-data-definitions';

/**
 * Whether a vocabulary's tags are read out of every answer by default.
 *
 * Reads `DEFAULT_EXTRACTION_FACETS` straight from the package — the same list the extractor builds
 * its allowed set from — so a tag control that hides these vocabularies and the extractor that reads
 * them can never disagree. There is no second copy of the list here.
 */
export function isExtractedByDefault(facet: TaxonomyFacet): boolean {
  return DEFAULT_EXTRACTION_FACETS.includes(facet);
}

/**
 * The extracted-by-default vocabularies the account actually holds, in the package's canonical
 * order.
 *
 * `accountFacets` is every facet the account carries a vocabulary for; the result is the subset that
 * is also extracted by default, so a caller can name exactly those — an account with no programs is
 * never told about programs — and offer nothing the account would not recognise.
 */
export function heldExtractedByDefaultFacets(accountFacets: Iterable<TaxonomyFacet>): TaxonomyFacet[] {
  const held = new Set(accountFacets);
  return DEFAULT_EXTRACTION_FACETS.filter((facet) => held.has(facet));
}

/** Joins vocabulary names into one readable phrase: "Brands", "Brands and Products", "Brands, Products and Programs". */
export function formatFacetNameList(names: string[]): string {
  if (names.length <= 1) {
    return names.join('');
  }

  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** The vocabularies a prompt can be told to read out of its answers. */
export const EXTRACTION_TARGET_FACETS: TaxonomyFacet[] = [
  TaxonomyFacet.AuthoritySource,
  TaxonomyFacet.Competitor,
  TaxonomyFacet.CompetitorProduct,
  TaxonomyFacet.Brand,
  TaxonomyFacet.Product,
  TaxonomyFacet.ProductFeature,
  TaxonomyFacet.AcademicUnit,
];

/**
 * The vocabularies an extraction-target control OFFERS: never one Sync Core already reads out of
 * every answer, because offering it would invite the customer to attach a tag that is extracted
 * automatically. Only the offered options shrink — a tag already attached stays rendered and
 * removable.
 */
export const OFFERED_EXTRACTION_TARGET_FACETS: TaxonomyFacet[] = EXTRACTION_TARGET_FACETS.filter((facet) => !isExtractedByDefault(facet));
